"use client";

import { trackEvent } from "@/utils/analytics";
import { TikTokIcon } from "@/components/icons";
import { FooterLink } from "./FooterLink";
import { FooterColumn } from "./FooterColumn";

interface FooterSectionProps {
  className?: string;
}

export default function FooterSection({ className = "" }: FooterSectionProps) {
  return (
    <footer
      className={`py-12 bg-background border-t border-border ${className}`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div className="text-center md:text-left">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Build-a-Dress
            </h3>
            <p className="text-muted-foreground mb-4">
              Custom dress design and production, founded in Sammamish,
              Washington.
            </p>
            <div className="space-y-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Contact us:</p>
                <a
                  href={`mailto:${process.env.NEXT_PUBLIC_BUSINESS_EMAIL || "support@build-a-dress.com"}`}
                  className="inline-flex min-h-11 items-center text-info hover:text-info text-sm font-medium"
                >
                  {process.env.NEXT_PUBLIC_BUSINESS_EMAIL ||
                    "support@build-a-dress.com"}
                </a>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Follow us:</p>
                <a
                  href="https://www.tiktok.com/@buildadress"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors inline-flex min-h-11 items-center gap-2 text-sm font-medium"
                  aria-label="Follow us on TikTok"
                >
                  <TikTokIcon size={20} />
                  <span>TikTok</span>
                </a>
              </div>
            </div>
          </div>

          <FooterColumn title="Quick Links">
            <li>
              <FooterLink href="/track" className="font-medium">
                📦 Track Your Order
              </FooterLink>
            </li>
            <li>
              <FooterLink href="/create">Start Designing</FooterLink>
            </li>
            <li>
              <FooterLink href="/gallery">Browse Gallery</FooterLink>
            </li>
            <li>
              <FooterLink href="/blog">Blog</FooterLink>
            </li>
            <li>
              <FooterLink href="/research">Research</FooterLink>
            </li>
            <li>
              <FooterLink href="/faq">FAQ</FooterLink>
            </li>
            <li>
              <FooterLink
                href="/credits"
                onClick={() => {
                  try {
                    trackEvent("purchase_credits_click", {
                      source: "footer",
                      page_section: "credits",
                    });
                  } catch {
                    // analytics must never block UX
                  }
                }}
              >
                Buy Credits
              </FooterLink>
            </li>
            <li>
              <FooterLink href="/artisans">Meet Our Artisans</FooterLink>
            </li>
          </FooterColumn>

          <FooterColumn title="Dress Categories">
            <li>
              <FooterLink href="/custom-wedding-dresses">
                Wedding Dresses
              </FooterLink>
            </li>
            <li>
              <FooterLink href="/custom-hijabi-wedding-dresses">
                Hijabi Dresses
              </FooterLink>
            </li>
            <li>
              <FooterLink href="/custom-modest-dresses">
                Modest Dresses
              </FooterLink>
            </li>
            <li>
              <FooterLink href="/custom-prom-dresses">Prom Dresses</FooterLink>
            </li>
            <li>
              <FooterLink href="/custom-quinceanera-dresses">
                Quinceanera Dresses
              </FooterLink>
            </li>
            <li>
              <FooterLink href="/custom-bridesmaid-dresses">
                Bridesmaid Dresses
              </FooterLink>
            </li>
            <li>
              <FooterLink href="/custom-formal-dresses">
                Formal Dresses
              </FooterLink>
            </li>
          </FooterColumn>

          <FooterColumn title="Legal & Support">
            <li>
              <FooterLink href="/service-agreement">
                Service Agreement
              </FooterLink>
            </li>
            <li>
              <FooterLink href="/refund-policy">
                Return & Refund Policy
              </FooterLink>
            </li>
            <li>
              <FooterLink href="/privacy-policy">Privacy Policy</FooterLink>
            </li>
          </FooterColumn>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-border pt-8 text-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Build-a-Dress. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
